import {BaseElement, BaseElementAsync, MsgLabel, OSkinableInit} from "back/commons/basis";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {IReg, REG} from "lib/commons/registry";
import {JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IDepotUiEnv} from "lib/store/depot";
import {JNodeShortProps} from "lib/store/urlTree";
import {OBrowseBoardInit} from "back/store/views/browseBoard";
import {AreaAsync, IArea} from "lib/commons/areas";
import {IInfo, IInfoBroker, IInfoConsumer, InfoBrokerBasic} from "lib/commons/infos";
import {InfoCurrentRes, resPath} from "lib/store/res";
import {IO} from "lib/commons/io/io";
import {JLastDatas} from "lib/commons/lastDatas";
import {IResViewable} from "back/store/views/resViewer";
import {IView, IViewContainer, OViewVisitOptions} from "lib/commons/views";
import {ICloseable} from "back/commons/widgets/popupable";
import {AddFolderInList} from "back/store/actions/depotActions";

/**
 * Dialogue de sélection d'un dossier ou d'une ressource via
 * les différents Boards (browse et/ou search) pour trouver une ressource.
 */
export interface SelectRes extends BaseElement {
	initialize(init: OSelectResInit): this
}

export interface OSelectResInit extends OSkinableInit {

	initialPath?: resPath

	/**
	 * Board(s) à afficher pour permettre la sélection.
	 * Chaque Board doit exploiter le SelectRes.infoBroker pour notifier le InfoCurrentRes
	 * indiquant le noeud courant.
	 */
	boards: IArea<SelectRes, HTMLElement & Partial<IResViewable>>[]

	/**
	 * Accepte la sélection (true) ou la refuse avec un msg pour le user (string).
	 * Note : peut aussi être utilisé pour ajouter un autre bouton custom de validation
	 * ("Mettre à jour ce dossier", "Ajouter un dossier fils", ...)
	 */
	checkSel?: (selectRes: SelectRes) => true | string

	/** Contexte d'appel auprès duquel retourner le résultat de la sélection. */
	holder?: ICloseable<JNodeShortProps | null>

	/** Label du bouton de sélection. "Sélectionner" par défaut */
	selBtnLabel?: string
}

export class SelectRes extends BaseElementAsync implements IInfoConsumer, IViewContainer {

	reg: IReg<IDepotUiEnv>;

	checkSel?: (utSelector: SelectRes) => true | string

	infoBroker?: IInfoBroker;

	/** Widgets */
	view: HTMLElement;
	footer: HTMLElement;
	doBtn: Button;
	msg: MsgLabel;

	currentSel: JNodeShortProps;

	get holder(): ICloseable<JNodeShortProps | null> {return this._holder || POPUP.findPopupableParent(this)}

	_holder?: ICloseable<JNodeShortProps | null>

	protected async _initialize(init: OSelectResInit) {
		this.reg = this.findReg(init);
		this.checkSel = init.checkSel;
		this._holder = init.holder;
		this.infoBroker = new InfoBrokerBasic();
		this.infoBroker.addConsumer(this);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);
		const boards = init.boards.filter((board) => board.isVisible(this));
		const root = sr.appendChild(<main/>);
		this.footer = sr.appendChild(<div id="footer"/>);
		this.msg = this.footer.appendChild(<c-msg id="msg"/>) as MsgLabel;
		this.doBtn = this.footer.appendChild(<Button id="do" class="default" disabled ui-context="dialog" label={init.selBtnLabel || "Sélectionner"} onclick={this.onDoBtn}/>) as Button;
		this.footer.appendChild(<Button id="cancel" ui-context="dialog" label="Annuler" onclick={this.onCancelBtn}/>);
		this.checkSelValid();
		if (boards.length > 1) {
			console.trace("TODO multi-boards tabs");
		} else if (boards.length === 0) {
			this.cancel();
		} else {
			const elt = this.view = await boards[0].loadBody(this);
			root.appendChild(elt);
			this.checkSelValid();
			if (init.initialPath != null && elt.setResPath) {
				elt.setResPath(init.initialPath);
			}
		}
	}

	onInfo(info: IInfo) {
		if (info instanceof InfoCurrentRes) {
			this.currentSel = info.nodeProps;
			this.checkSelValid();
		}
	}

	checkSelValid(): boolean {
		let valid: true | string = true;
		if (this.currentSel == null) {
			valid = "Sélectionnez un contenu";
		} else if (this.checkSel) {
			valid = this.checkSel(this);
		}
		this.doBtn.disabled = valid !== true;
		this.msg.setCustomMsg(valid !== true ? valid : null, "warning");
		return valid === true;
	}

	validAndDo() {
		if (this.checkSelValid()) this.holder.close(this.currentSel);
	}

	cancel() {
		this.holder?.close();
	}

	onCancelBtn(this: Button) {
		DOMSH.findHost<SelectRes>(this).cancel();
	}

	onDoBtn(this: Button) {
		DOMSH.findHost<SelectRes>(this).validAndDo();
	}

	visitViews(visitor: (view: IView) => any): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}
}

REG.reg.registerSkin('store-select-res', 1, /* language=CSS */ `
	main {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}
`);

customElements.define("store-select-res", SelectRes);


export class SelectResBrowseArea extends AreaAsync<SelectRes> {

	constructor() {
		super();
		this.requireLib(IO.asEndPoint("/@back@/store/views/browseBoard.js"));
	}

	buildBody(ctx: SelectRes, lastDatas?: JLastDatas): HTMLElement {
		const init: OBrowseBoardInit = {
			reg: ctx.reg,
			infoBroker: ctx.infoBroker,
			selMono: true,
			utBrowser: {
				actions: [new AddFolderInList()]
			},
			resViewer: {
				keyView: "select"
			}
		}
		return <store-browse-board î={init}/>
	}
}